import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Play, Search, ArrowLeft, Crown, Filter, Grid, List,
  Mic2, Tv, Music, PartyPopper, Disc3, Film, ChevronDown, Video,
  Users, CheckCircle, BadgeCheck
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import VODFooter from '../components/VODFooter';

const API = process.env.REACT_APP_BACKEND_URL || '';

const VODBrowse = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [videos, setVideos] = useState([]);
  const [series, setSeries] = useState([]);
  const [creators, setCreators] = useState([]); // NEW: Creators list
  const [searchCreators, setSearchCreators] = useState([]); // NEW: Creator search results
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  
  // Filters
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [selectedTier, setSelectedTier] = useState(searchParams.get('tier') || '');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState(searchParams.get('view') || 'videos'); // 'videos', 'series', or 'creators'

  useEffect(() => {
    fetchCategories();
    fetchSeries();
    fetchCreators(); // NEW: Fetch all creators
  }, []);

  useEffect(() => {
    fetchVideos();
    // Also search creators when there's a search query
    if (searchQuery && viewMode !== 'creators') {
      searchCreatorsQuery(searchQuery);
    } else {
      setSearchCreators([]);
    }
  }, [selectedCategory, selectedTier, searchQuery, currentPage]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/api/vod/categories`);
      setCategories(res.data.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchSeries = async () => {
    try {
      const token = localStorage.getItem('livestreamToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/api/vod/series`, { headers });
      setSeries(res.data.series || []);
    } catch (err) {
      console.error('Error fetching series:', err);
    }
  };

  // NEW: Fetch all creators
  const fetchCreators = async () => {
    try {
      const res = await axios.get(`${API}/api/creators/profiles?limit=50`);
      setCreators(res.data.profiles || []);
    } catch (err) {
      console.error('Error fetching creators:', err);
    }
  };

  // NEW: Search creators by name
  const searchCreatorsQuery = async (query) => {
    if (!query || query.length < 2) {
      setSearchCreators([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/api/creators/search?q=${encodeURIComponent(query)}&limit=5`);
      setSearchCreators(res.data.creators || []);
    } catch (err) {
      console.error('Error searching creators:', err);
      setSearchCreators([]);
    }
  };

  const fetchVideos = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('livestreamToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedTier) params.append('tier', selectedTier);
      if (searchQuery) params.append('search', searchQuery);
      params.append('page', currentPage.toString());
      params.append('limit', '24');

      const res = await axios.get(`${API}/api/vod/videos?${params.toString()}`, { headers });
      setVideos(res.data.videos || []);
      setTotalPages(res.data.pages || 1);
    } catch (err) {
      console.error('Error fetching videos:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    // Update URL params
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (selectedCategory) params.set('category', selectedCategory);
    if (selectedTier) params.set('tier', selectedTier);
    setSearchParams(params);
  };

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1);
  };

  const handleTierChange = (tier) => {
    setSelectedTier(tier);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSelectedCategory('');
    setSelectedTier('');
    setSearchQuery('');
    setCurrentPage(1);
    setSearchParams({});
  };

  const getCategoryIcon = (name) => {
    const icons = {
      'Podcasts': <Mic2 className="h-4 w-4" />,
      'Shows': <Tv className="h-4 w-4" />,
      'Concerts': <Music className="h-4 w-4" />,
      'Entertainment': <PartyPopper className="h-4 w-4" />,
      'DJ Videos': <Disc3 className="h-4 w-4" />,
    };
    return icons[name] || <Film className="h-4 w-4" />;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const hrs = Math.floor(mins / 60);
    if (hrs > 0) return `${hrs}h ${mins % 60}m`;
    return `${mins}m`;
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? cat.name : '';
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/95 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          {/* Top row - Back, Logo, Filters */}
          <div className="flex items-center justify-between gap-2 sm:gap-3 mb-3">
            <div className="flex items-center gap-2 sm:gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/vod')}
                className="text-gray-400 hover:text-white px-2"
              >
                <ArrowLeft className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">Back</span>
              </Button>
              
              <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/vod')}>
                <img 
                  src="/ltv-icon.png" 
                  alt="LTV Logo" 
                  className="w-7 h-7 sm:w-8 sm:h-8 object-contain brightness-0 invert"
                />
                <span className="font-bold text-sm sm:text-base">LTV VOD</span>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => navigate('/creator')}
                className="hidden md:flex bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-semibold border-0"
              >
                <Video className="h-4 w-4 mr-1" />
                Creator Package
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="border-white/20"
              >
                <Filter className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Filters</span>
                {(selectedCategory || selectedTier) && (
                  <Badge className="ml-1 sm:ml-2 bg-purple-500 text-xs">
                    {(selectedCategory ? 1 : 0) + (selectedTier ? 1 : 0)}
                  </Badge>
                )}
              </Button>
            </div>
          </div>
          
          {/* Search - Full width */}
          <form onSubmit={handleSearch} className="w-full">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input
                type="text"
                placeholder="Search videos, creators..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 bg-white/10 border-white/20 text-white placeholder:text-gray-500"
              />
            </div>
          </form>
          
          {/* Filters Panel */}
          {showFilters && (
            <div className="mt-4 p-4 bg-white/5 rounded-xl border border-white/10">
              <div className="flex flex-wrap gap-4">
                {/* Category Filter */}
                <div>
                  <label className="text-sm text-gray-400 mb-2 block">Category</label>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant={selectedCategory === '' ? 'default' : 'outline'}
                      onClick={() => handleCategoryChange('')}
                      className={selectedCategory === '' ? 'bg-purple-600' : 'border-white/20'}
                    >
                      All
                    </Button>
                    {categories.map((cat) => (
                      <Button
                        key={cat.id}
                        size="sm"
                        variant={selectedCategory === cat.id ? 'default' : 'outline'}
                        onClick={() => handleCategoryChange(cat.id)}
                        className={selectedCategory === cat.id ? 'bg-purple-600' : 'border-white/20'}
                      >
                        {getCategoryIcon(cat.name)}
                        <span className="ml-1">{cat.name}</span>
                      </Button>
                    ))}
                  </div>
                </div>
                
                {/* Tier Filter */}
                <div>
                  <label className="text-sm text-gray-400 mb-2 block">Content Tier</label>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant={selectedTier === '' ? 'default' : 'outline'}
                      onClick={() => handleTierChange('')}
                      className={selectedTier === '' ? 'bg-purple-600' : 'border-white/20'}
                    >
                      All
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedTier === 'basic' ? 'default' : 'outline'}
                      onClick={() => handleTierChange('basic')}
                      className={selectedTier === 'basic' ? 'bg-purple-600' : 'border-white/20'}
                    >
                      Basic
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedTier === 'premium' ? 'default' : 'outline'}
                      onClick={() => handleTierChange('premium')}
                      className={selectedTier === 'premium' ? 'bg-gradient-to-r from-purple-600 to-pink-600' : 'border-white/20'}
                    >
                      <Crown className="h-3 w-3 mr-1" />
                      Premium
                    </Button>
                  </div>
                </div>
                
                {/* Clear Filters */}
                {(selectedCategory || selectedTier || searchQuery) && (
                  <div className="flex items-end">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={clearFilters}
                      className="text-red-400 hover:text-red-300"
                    >
                      Clear All
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-6 sm:py-8">
        {/* Creator Search Results - Show at top when searching */}
        {searchQuery && searchCreators.length > 0 && viewMode !== 'creators' && (
          <div className="mb-6 p-4 bg-purple-900/20 rounded-xl border border-purple-500/30">
            <h3 className="text-sm font-medium text-purple-300 mb-3 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Creators matching "{searchQuery}"
            </h3>
            <div className="flex gap-4 overflow-x-auto pb-2">
              {searchCreators.map((creator) => (
                <div
                  key={creator.id}
                  onClick={() => navigate(`/c/${creator.slug}`)}
                  className="flex-shrink-0 flex items-center gap-3 px-3 py-2 bg-white/5 rounded-lg cursor-pointer hover:bg-white/10 transition"
                >
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-800">
                    {creator.profile_image_url ? (
                      <img src={creator.profile_image_url} alt={creator.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-purple-400 font-bold">
                        {creator.name?.charAt(0) || 'C'}
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-white flex items-center gap-1">
                      {creator.name}
                      {creator.is_verified && <BadgeCheck className="w-4 h-4 text-purple-400" />}
                    </p>
                    <p className="text-xs text-gray-400">{creator.subscriber_count?.toLocaleString() || 0} subscribers</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* View Toggle - Videos, Series, or Creators */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <Button
            size="sm"
            variant={viewMode === 'videos' ? 'default' : 'outline'}
            onClick={() => {
              setViewMode('videos');
              setSearchParams(prev => {
                const params = new URLSearchParams(prev);
                params.delete('view');
                return params;
              });
            }}
            className={`${viewMode === 'videos' ? 'bg-purple-600' : 'border-white/20'} whitespace-nowrap`}
          >
            <Film className="h-4 w-4 mr-2" />
            Videos
          </Button>
          <Button
            size="sm"
            variant={viewMode === 'series' ? 'default' : 'outline'}
            onClick={() => {
              setViewMode('series');
              setSearchParams({ view: 'series' });
            }}
            className={`${viewMode === 'series' ? 'bg-purple-600' : 'border-white/20'} whitespace-nowrap`}
          >
            <Tv className="h-4 w-4 mr-2" />
            Series
          </Button>
          <Button
            size="sm"
            variant={viewMode === 'creators' ? 'default' : 'outline'}
            onClick={() => {
              setViewMode('creators');
              setSearchParams({ view: 'creators' });
            }}
            className={`${viewMode === 'creators' ? 'bg-purple-600' : 'border-white/20'} whitespace-nowrap`}
          >
            <Users className="h-4 w-4 mr-2" />
            Creators
          </Button>
        </div>

        {/* Creators View */}
        {viewMode === 'creators' ? (
          <div>
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold">Our Creators</h1>
                <p className="text-gray-500 text-sm mt-1">
                  {creators.length} creator{creators.length !== 1 ? 's' : ''} on the platform
                </p>
              </div>
            </div>
            
            {creators.length === 0 ? (
              <div className="text-center py-12 sm:py-20 px-4">
                <div className="bg-white/5 rounded-2xl p-8 sm:p-12 max-w-md mx-auto border border-white/10">
                  <Users className="h-12 w-12 sm:h-16 sm:w-16 mx-auto text-purple-500/50 mb-4" />
                  <h3 className="text-lg sm:text-xl font-semibold mb-2">No creators yet</h3>
                  <p className="text-gray-500 text-sm sm:text-base">Check back later for new creators</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 sm:gap-6">
                {creators.map((creator) => (
                  <div
                    key={creator.id}
                    onClick={() => navigate(`/c/${creator.slug}`)}
                    className="cursor-pointer group text-center"
                  >
                    {/* Profile Image */}
                    <div className="relative mb-3">
                      <div className="w-24 h-24 sm:w-32 sm:h-32 mx-auto rounded-full overflow-hidden bg-gray-800 border-2 border-transparent group-hover:border-purple-500 transition-all duration-300">
                        {creator.profile_image_url ? (
                          <img 
                            src={creator.profile_image_url} 
                            alt={creator.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-2xl font-bold text-purple-400 bg-gradient-to-br from-purple-900/50 to-pink-900/50">
                            {creator.name?.charAt(0) || 'C'}
                          </div>
                        )}
                      </div>
                      {creator.is_verified && (
                        <div className="absolute bottom-1 right-1/4 w-5 h-5 bg-purple-600 rounded-full flex items-center justify-center border-2 border-black">
                          <BadgeCheck className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                    
                    {/* Creator Info */}
                    <h3 className="font-semibold text-white truncate group-hover:text-purple-400 transition-colors text-sm sm:text-base">
                      {creator.name}
                    </h3>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {creator.subscriber_count?.toLocaleString() || 0} subscribers
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : viewMode === 'series' ? (
          <div>
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold">All Series</h1>
                <p className="text-gray-500 text-sm mt-1">
                  {series.length} series available
                </p>
              </div>
            </div>
            
            {series.length === 0 ? (
              <div className="text-center py-12 sm:py-20 px-4">
                <div className="bg-white/5 rounded-2xl p-8 sm:p-12 max-w-md mx-auto border border-white/10">
                  <Tv className="h-12 w-12 sm:h-16 sm:w-16 mx-auto text-purple-500/50 mb-4" />
                  <h3 className="text-lg sm:text-xl font-semibold mb-2">No series available</h3>
                  <p className="text-gray-500 text-sm sm:text-base">Check back later for new series</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
                {series.map((s) => (
                  <SeriesCard 
                    key={s.id} 
                    series={s} 
                    onClick={() => navigate(`/vod/series/${s.id}`)}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Series Section (only show when no filters are applied and in videos view) */}
            {!searchQuery && !selectedCategory && !selectedTier && series.length > 0 && (
              <div className="mb-8 sm:mb-10">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg sm:text-xl font-bold flex items-center gap-2">
                    <Tv className="h-4 w-4 sm:h-5 sm:w-5 text-purple-400" />
                    Series
                  </h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setViewMode('series');
                      setSearchParams({ view: 'series' });
                    }}
                    className="text-purple-400 text-sm"
                  >
                    View All <ChevronDown className="ml-1 h-4 w-4 rotate-[-90deg]" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
                  {series.slice(0, 5).map((s) => (
                    <SeriesCard 
                      key={s.id} 
                      series={s} 
                      onClick={() => navigate(`/vod/series/${s.id}`)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Results Header */}
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">
              {searchQuery ? `Results for "${searchQuery}"` : 
               selectedCategory ? getCategoryName(selectedCategory) : 
               'All Videos'}
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              {videos.length} video{videos.length !== 1 ? 's' : ''} found
            </p>
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-16 sm:py-20">
            <div className="w-10 h-10 sm:w-12 sm:h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : videos.length === 0 ? (
          <div className="text-center py-12 sm:py-20 px-4">
            <div className="bg-white/5 rounded-2xl p-8 sm:p-12 max-w-md mx-auto border border-white/10">
              <Film className="h-12 w-12 sm:h-16 sm:w-16 mx-auto text-purple-500/50 mb-4" />
              <h3 className="text-lg sm:text-xl font-semibold mb-2">No videos found</h3>
              <p className="text-gray-500 text-sm sm:text-base mb-4">Try adjusting your filters or search query</p>
              <Button onClick={clearFilters} className="bg-purple-600 hover:bg-purple-500">
                Clear Filters
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Video Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
              {videos.map((video) => (
                <VideoCard 
                  key={video.id} 
                  video={video} 
                  onClick={() => navigate(`/vod/v/${video.slug || video.id}?autoplay=true`)}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6 sm:mt-8">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="border-white/20"
                >
                  Previous
                </Button>
                <span className="text-gray-400 px-3 sm:px-4 text-sm">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="border-white/20"
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
          </>
        )}
      </main>
      
      {/* Footer */}
      <VODFooter />
    </div>
  );
};

// Video Card Component
const VideoCard = ({ video, onClick }) => {
  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const hrs = Math.floor(mins / 60);
    if (hrs > 0) return `${hrs}h ${mins % 60}m`;
    return `${mins}m`;
  };

  return (
    <div onClick={onClick} className="group cursor-pointer">
      <div className="relative aspect-video rounded-xl overflow-hidden bg-gray-900 mb-2">
        {video.thumbnail_url ? (
          <img 
            src={video.thumbnail_url} 
            alt={video.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-900/50 to-pink-900/50">
            <Play className="h-12 w-12 text-white/30" />
          </div>
        )}
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        
        {/* Play button on hover */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-12 h-12 rounded-full bg-purple-600/90 flex items-center justify-center">
            <Play className="h-6 w-6 text-white ml-1" fill="white" />
          </div>
        </div>
        
        {/* Badges - Top left */}
        <div className="absolute top-2 left-2 flex gap-1">
          {/* 4K Badge */}
          {video.has_4k && (
            <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-black text-xs font-bold px-2">
              4K
            </Badge>
          )}
          
          {/* Tier badge */}
          {video.tier === 'premium' && (
            <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-xs">
              <Crown className="h-3 w-3 mr-1" /> Premium
            </Badge>
          )}
        </div>
        
        {/* Access badge */}
        {video.has_access && (
          <Badge className="absolute top-2 right-2 bg-green-600 text-xs">
            Owned
          </Badge>
        )}
        
        {/* Duration */}
        {video.duration > 0 && (
          <span className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-2 py-1 rounded">
            {formatDuration(video.duration)}
          </span>
        )}
        
        {/* Price - Only show for Premium tier */}
        {video.tier === 'premium' && !video.has_access && (
          <span className="absolute bottom-2 left-2 bg-black/80 text-white text-xs px-2 py-1 rounded">
            £{video.price?.toFixed(2)}
          </span>
        )}
      </div>
      
      <h3 className="font-semibold text-sm line-clamp-2 group-hover:text-purple-400 transition-colors">
        {video.title}
      </h3>
    </div>
  );
};

// Series Card Component
const SeriesCard = ({ series, onClick }) => {
  return (
    <div onClick={onClick} className="group cursor-pointer">
      <div className="relative aspect-video rounded-xl overflow-hidden bg-gray-900 mb-2">
        {series.thumbnail_url ? (
          <img 
            src={series.thumbnail_url} 
            alt={series.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-900/50 to-pink-900/50">
            <Tv className="h-12 w-12 text-white/30" />
          </div>
        )}
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
        
        {/* Series badge */}
        <Badge className="absolute top-2 left-2 bg-blue-600 text-xs">
          <Tv className="h-3 w-3 mr-1" /> Series
        </Badge>
        
        {/* Tier badge */}
        {series.tier === 'premium' && (
          <Badge className="absolute top-2 right-2 bg-gradient-to-r from-purple-500 to-pink-500 text-xs">
            <Crown className="h-3 w-3 mr-1" />
          </Badge>
        )}
        
        {/* Episode count */}
        <div className="absolute bottom-2 left-2 right-2 flex justify-between items-center">
          <span className="bg-black/80 text-white text-xs px-2 py-1 rounded">
            {series.season_count || 0} Season{(series.season_count || 0) !== 1 ? 's' : ''}
          </span>
          <span className="bg-black/80 text-white text-xs px-2 py-1 rounded">
            {series.episode_count || 0} Ep
          </span>
        </div>
      </div>
      
      <h3 className="font-semibold text-sm line-clamp-2 group-hover:text-purple-400 transition-colors">
        {series.title}
      </h3>
    </div>
  );
};

export default VODBrowse;
